import React, { useCallback, useEffect, useState } from 'react';
import {
  ComposedChart, Bar, Line, AreaChart, Area, LineChart, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import { authHeaders } from './Admin';

import { API_BASE } from '../apiBase';

const C = {
  panel: '#141416',
  panelAlt: '#1a1a1d',
  border: '#262629',
  text: '#f2f2f3',
  sub: '#8a8a90',
  accent: '#2D9E5F',
};

// What we pay Apple Ads per attributed install, on average. Estimate only;
// the real spend lives in the Apple Ads dashboard.
const CPI_USD = 5.5;

interface DailyRow {
  date: string;
  new_users: number;
  ad_installs: number;
  organic: number;
  trials_started: number;
  expected_revenue_usd: number;
  cancelled: number;
  lost_revenue_usd: number;
}

const RANGES = [7, 14, 30, 60, 90] as const;

type ChartStyle = 'bars' | 'lines' | 'cumulative';
const CHART_STYLES: { key: ChartStyle; label: string }[] = [
  { key: 'cumulative', label: 'Running total' },
  { key: 'bars', label: 'Daily bars' },
  { key: 'lines', label: 'Daily lines' },
];

// Series names double as chart legend labels, so they match the table headers.
const S_EXPECTED = 'Expected trial revenue';
const S_LOST = 'Lost revenue (cancelled)';
const S_NET = 'Expected net income';
const S_SPEND = 'Ad spend';

function DailyReportTab({ token, isMobile }: { token: string; isMobile: boolean }) {
  const [days, setDays] = useState<number>(7);
  const [chartStyle, setChartStyle] = useState<ChartStyle>('cumulative');
  const [rows, setRows] = useState<DailyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/daily-report?days=${days}`, { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // API envelope is {success, data:{days}}; tolerate a bare {days} too.
      setRows((data.data && data.data.days) || data.days || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    }
    setLoading(false);
  }, [token, days]);

  useEffect(() => { load(); }, [load]);

  const totals = rows.reduce(
    (a, r) => ({
      users: a.users + r.new_users,
      organic: a.organic + r.organic,
      ads: a.ads + r.ad_installs,
      trials: a.trials + r.trials_started,
      rev: a.rev + r.expected_revenue_usd,
      cancelled: a.cancelled + r.cancelled,
      lost: a.lost + r.lost_revenue_usd,
    }),
    { users: 0, organic: 0, ads: 0, trials: 0, rev: 0, cancelled: 0, lost: 0 }
  );

  const money = (n: number) => `$${n.toFixed(2)}`;

  // Chart wants chronological order (the table is newest-first) and the
  // lost line below zero so the money story reads at a glance.
  const chartData = rows
    .slice()
    .reverse()
    .map((r) => ({
      day: new Date(r.date + 'T00:00:00Z').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'Asia/Karachi' }),
      [S_SPEND]: +(r.ad_installs * CPI_USD).toFixed(2),
      [S_EXPECTED]: +r.expected_revenue_usd.toFixed(2),
      [S_LOST]: +(-r.lost_revenue_usd).toFixed(2),
      [S_NET]: +(r.expected_revenue_usd - r.lost_revenue_usd).toFixed(2),
    }));

  // Running totals across the selected window: how the period adds up.
  let cumE = 0, cumL = 0, cumS = 0;
  const cumulativeData = chartData.map((d) => {
    cumE += d[S_EXPECTED];
    cumL += d[S_LOST];
    cumS += d[S_SPEND];
    return {
      day: d.day,
      [S_SPEND]: +cumS.toFixed(2),
      [S_EXPECTED]: +cumE.toFixed(2),
      [S_LOST]: +cumL.toFixed(2),
      [S_NET]: +(cumE + cumL).toFixed(2),
    };
  });
  const karachiToday = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Karachi' });
  const fmtDate = (iso: string) => {
    const d = new Date(iso + 'T00:00:00Z');
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'Asia/Karachi' });
  };

  const th: React.CSSProperties = {
    textAlign: 'right', padding: '13px 12px', fontSize: 12, color: C.sub,
    fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, lineHeight: 1.35, verticalAlign: 'bottom',
  };
  const td: React.CSSProperties = {
    textAlign: 'right', padding: '13px 12px', fontSize: 14.5, color: C.text,
    borderTop: `1px solid ${C.border}`, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
  };
  // Empty gap between the user-count columns and the money columns.
  const sep: React.CSSProperties = { paddingLeft: 56 };

  const chartDefs = (
    <defs>
      <linearGradient id="dr-gain" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#34D399" stopOpacity={0.95} />
        <stop offset="100%" stopColor="#34D399" stopOpacity={0.45} />
      </linearGradient>
      <linearGradient id="dr-loss" x1="0" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor="#F87171" stopOpacity={0.95} />
        <stop offset="100%" stopColor="#F87171" stopOpacity={0.45} />
      </linearGradient>
      <linearGradient id="dr-area-gain" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#34D399" stopOpacity={0.35} />
        <stop offset="100%" stopColor="#34D399" stopOpacity={0.02} />
      </linearGradient>
      <linearGradient id="dr-area-loss" x1="0" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor="#F87171" stopOpacity={0.35} />
        <stop offset="100%" stopColor="#F87171" stopOpacity={0.02} />
      </linearGradient>
    </defs>
  );

  const chartAxes = (
    <>
      <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="day" tick={{ fill: C.sub, fontSize: 11.5 }} tickLine={false} axisLine={{ stroke: C.border }} minTickGap={24} />
      <YAxis tick={{ fill: C.sub, fontSize: 11.5 }} tickLine={false} axisLine={false} tickFormatter={(v: number) => `$${v}`} width={52} />
      <Tooltip
        formatter={(v) => `$${Math.abs(Number(v)).toFixed(2)}`}
        cursor={{ fill: 'rgba(255,255,255,0.04)' }}
        contentStyle={{ background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 10, fontSize: 12.5 }}
        labelStyle={{ color: C.text, fontWeight: 700 }}
      />
      <Legend wrapperStyle={{ fontSize: 12.5 }} iconType="circle" />
    </>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, color: C.text }}>Daily report</h2>
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setDays(r)}
              style={{
                padding: '6px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                border: `1px solid ${days === r ? C.accent : C.border}`,
                background: days === r ? C.accent : 'transparent',
                color: days === r ? '#fff' : C.sub,
              }}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>
      <p style={{ color: C.sub, fontSize: 12.5, margin: '0 0 16px', lineHeight: 1.6 }}>
        Days run on Asia/Karachi time. <strong>Ad spend</strong> assumes ${CPI_USD.toFixed(2)} per ad install; the real number lives in the
        Apple Ads dashboard. <strong>Expected trial revenue</strong> is what that day's trials bill if every one converts (monthly $3.99; annual $29.99 for trials started before 30 Oct 2026 and $39.99 after),
        <strong> lost revenue</strong> is the same list price walking away when auto-renew goes off, and <strong>expected net income</strong> is the
        difference. Expected figures are potential, not money in the bank.
      </p>

      {error && <div style={{ color: '#f87171', fontSize: 13.5, marginBottom: 12 }}>{error}</div>}
      {loading && rows.length === 0 && <div style={{ color: C.sub, fontSize: 13.5 }}>Loading…</div>}

      {rows.length > 0 && (
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px 8px', marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginBottom: 6 }}>
            {CHART_STYLES.map((cs) => (
              <button
                key={cs.key}
                onClick={() => setChartStyle(cs.key)}
                style={{
                  padding: '5px 11px', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  border: `1px solid ${chartStyle === cs.key ? C.accent : C.border}`,
                  background: chartStyle === cs.key ? C.accent : 'transparent',
                  color: chartStyle === cs.key ? '#fff' : C.sub,
                }}
              >
                {cs.label}
              </button>
            ))}
          </div>
          <div style={{ height: isMobile ? 250 : 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              {chartStyle === 'bars' ? (
                <ComposedChart data={chartData} margin={{ top: 4, right: 10, left: 0, bottom: 0 }} barGap={2}>
                  {chartDefs}
                  {chartAxes}
                  <ReferenceLine y={0} stroke={C.sub} strokeOpacity={0.5} />
                  <Bar dataKey={S_EXPECTED} fill="url(#dr-gain)" radius={[4, 4, 0, 0]} maxBarSize={26} />
                  <Bar dataKey={S_LOST} fill="url(#dr-loss)" radius={[0, 0, 4, 4]} maxBarSize={26} />
                  <Line type="monotone" dataKey={S_NET} stroke="#f2f2f3" strokeWidth={2.5}
                    dot={{ r: 3, fill: C.panel, stroke: '#f2f2f3', strokeWidth: 2 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey={S_SPEND} stroke="#FBBF24" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                </ComposedChart>
              ) : chartStyle === 'lines' ? (
                <LineChart data={chartData} margin={{ top: 4, right: 10, left: 0, bottom: 0 }}>
                  {chartDefs}
                  {chartAxes}
                  <ReferenceLine y={0} stroke={C.sub} strokeOpacity={0.5} />
                  <Line type="monotone" dataKey={S_SPEND} stroke="#FBBF24" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                  <Line type="monotone" dataKey={S_EXPECTED} stroke="#34D399" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey={S_LOST} stroke="#F87171" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey={S_NET} stroke="#f2f2f3" strokeWidth={2.5} dot={false} />
                </LineChart>
              ) : (
                <AreaChart data={cumulativeData} margin={{ top: 4, right: 10, left: 0, bottom: 0 }}>
                  {chartDefs}
                  {chartAxes}
                  <ReferenceLine y={0} stroke={C.sub} strokeOpacity={0.5} />
                  <Area type="monotone" dataKey={S_EXPECTED} stroke="#34D399" strokeWidth={2} fill="url(#dr-area-gain)" />
                  <Area type="monotone" dataKey={S_LOST} stroke="#F87171" strokeWidth={2} fill="url(#dr-area-loss)" />
                  <Line type="monotone" dataKey={S_NET} stroke="#f2f2f3" strokeWidth={2.5}
                    dot={{ r: 2.5, fill: C.panel, stroke: '#f2f2f3', strokeWidth: 2 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey={S_SPEND} stroke="#FBBF24" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {rows.length > 0 && (
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 860 }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left' }}>Date</th>
                <th style={th}>New users</th>
                <th style={th}>Organic</th>
                <th style={th}>Ads</th>
                <th style={th}>Trials</th>
                <th style={th}>Cancelled</th>
                <th style={{ ...th, ...sep }}>Ad spend</th>
                <th style={th}>Expected trial revenue</th>
                <th style={th}>Lost revenue (cancelled)</th>
                <th style={th}>Expected net income</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isToday = r.date === karachiToday;
                return (
                  <tr key={r.date} style={{ background: isToday ? C.panelAlt : 'transparent' }}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: isToday ? 800 : 500 }}>
                      {fmtDate(r.date)}
                      {isToday && <span style={{ marginLeft: 8, fontSize: 10.5, color: C.accent, fontWeight: 800 }}>TODAY</span>}
                    </td>
                    <td style={{ ...td, fontWeight: 700 }}>{r.new_users}</td>
                    <td style={td}>{r.organic}</td>
                    <td style={td}>{r.ad_installs}</td>
                    <td style={{ ...td, color: r.trials_started ? '#3B82F6' : C.sub, fontWeight: r.trials_started ? 700 : 400 }}>{r.trials_started}</td>
                    <td style={{ ...td, color: r.cancelled ? '#F87171' : C.sub, fontWeight: r.cancelled ? 700 : 400 }}>{r.cancelled}</td>
                    <td style={{ ...td, ...sep, color: r.ad_installs ? '#FBBF24' : C.sub }}>{money(r.ad_installs * CPI_USD)}</td>
                    <td style={{ ...td, color: r.expected_revenue_usd ? '#34D399' : C.sub }}>{money(r.expected_revenue_usd)}</td>
                    <td style={{ ...td, color: r.lost_revenue_usd ? '#F87171' : C.sub }}>{r.lost_revenue_usd ? `-${money(r.lost_revenue_usd)}` : money(0)}</td>
                    {(() => {
                      const net = r.expected_revenue_usd - r.lost_revenue_usd;
                      return (
                        <td style={{ ...td, fontWeight: 700, color: net > 0 ? '#34D399' : net < 0 ? '#F87171' : C.sub }}>
                          {net < 0 ? `-${money(Math.abs(net))}` : money(net)}
                        </td>
                      );
                    })()}
                  </tr>
                );
              })}
              <tr style={{ background: C.panelAlt }}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 800, color: C.accent }}>Total ({days}d)</td>
                <td style={{ ...td, fontWeight: 800 }}>{totals.users}</td>
                <td style={{ ...td, fontWeight: 800 }}>{totals.organic}</td>
                <td style={{ ...td, fontWeight: 800 }}>{totals.ads}</td>
                <td style={{ ...td, fontWeight: 800, color: '#3B82F6' }}>{totals.trials}</td>
                <td style={{ ...td, fontWeight: 800, color: '#F87171' }}>{totals.cancelled}</td>
                <td style={{ ...td, ...sep, fontWeight: 800, color: '#FBBF24' }}>{money(totals.ads * CPI_USD)}</td>
                <td style={{ ...td, fontWeight: 800, color: '#34D399' }}>{money(totals.rev)}</td>
                <td style={{ ...td, fontWeight: 800, color: '#F87171' }}>{totals.lost ? `-${money(totals.lost)}` : money(0)}</td>
                {(() => {
                  const net = totals.rev - totals.lost;
                  return (
                    <td style={{ ...td, fontWeight: 800, color: net > 0 ? '#34D399' : net < 0 ? '#F87171' : C.sub }}>
                      {net < 0 ? `-${money(Math.abs(net))}` : money(net)}
                    </td>
                  );
                })()}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DailyReportTab;
